import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileDown, FileText, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, errMsg } from "./api";
import DetailSVG from "./DetailSVG";
import GlassPicker from "./GlassPicker";
import AssemblyNotes from "./AssemblyNotes";
import { InfoLine } from "./InfoLine";

const KEYS = [["typical_mullion", "Typical mullion"], ["typical_transom", "Typical transom"], ["mullion_sash", "Mullion to sash"], ["corner_outside", "90° outside corner"], ["corner_inside", "90° inside corner"], ["stack_joint", "Mullion extension"], ["anchor_bracket", "Anchor bracket"]];
const NEW_GLASS = /^[MLDT]-/;
const HIDE = ["system", "glass_inner_y", "warning"];
const withCurrent = (opts, id, pool) => (id && !opts.some((o) => o.id === id) ? [...(pool.filter((p) => p.id === id)), ...opts] : opts);

const ProfileSelect = ({ label, value, onChange, options, testid, allowNone }) => (
  <label className="space-y-1">
    <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? null : v)}>
      <SelectTrigger data-testid={testid} className="h-9 border-zinc-800 bg-zinc-900 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent className="max-h-72">
        {allowNone && <SelectItem value="none">Auto / parametric</SelectItem>}
        {options.map((p) => <SelectItem key={p.id} value={p.id} className="text-xs">{p.code} · {p.width_mm}×{p.depth_mm} · {p.weight_kg_m} kg/m</SelectItem>)}
      </SelectContent>
    </Select>
  </label>
);

export default function AssemblyDetails({ designId, result, inputs }) {
  const [mullions, setMullions] = useState([]);
  const [transomPool, setTransomPool] = useState([]);
  const [compat, setCompat] = useState({});
  const [sel, setSel] = useState({ mullion_id: result?.members[0]?.lib_id || null, transom_id: result?.members[1]?.lib_id || null, sash_id: null, sleeve_id: null, corner_id: null });
  const [glass, setGlass] = useState(NEW_GLASS.test(inputs?.glass_type || "") ? inputs.glass_type : "D-6FT-12-6FT");
  const [keys, setKeys] = useState(KEYS.map(([k]) => k));
  const [opts, setOpts] = useState({ system: "structural", sash_type: "top_hung", corner_angle: 90, plate_id: null, cap_id: null, subframe_id: null, bead_id: null });
  const setO = (k) => (v) => setOpts((o) => ({ ...o, [k]: v }));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const partsText = useMemo(() => (data?.parts ? Object.entries(data.parts).filter(([, v]) => v).map(([k, v]) => `${k} ${v}`).join(" · ") : ""), [data]);

  useEffect(() => {
    api.get("/profiles", { params: { role: "mullion", limit: 500 } }).then((r) => {
      const items = r.data.items.filter((p) => p.hollow && p.width_mm >= 40 && p.width_mm <= 130 && p.depth_mm >= 50 && p.depth_mm <= 350);
      setMullions(items);
      setSel((s) => (s.mullion_id ? s : { ...s, mullion_id: items.sort((a, b) => b.depth_mm - a.depth_mm)[Math.floor(items.length / 3)]?.id }));
    });
  }, []);
  useEffect(() => { api.get("/profiles", { params: { role: "transom", limit: 500 } }).then((r) => setTransomPool(r.data.items)); }, []);
  useEffect(() => {
    if (!sel.mullion_id) return;
    api.get(`/profiles/${sel.mullion_id}/compatible`).then((r) => setCompat(r.data)).catch(() => setCompat({}));
  }, [sel.mullion_id]);
  useEffect(() => {
    if (!sel.mullion_id || !keys.length) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/assembly", { ...sel, ...opts, keys, glass_id: glass, design_id: designId || undefined });
        setData(data);
      } catch (e) {
        toast.error(errMsg(e, "Could not build details"));
      }
    }, 300);
    return () => clearTimeout(t);
  }, [sel, opts, keys, glass, designId]);

  const download = async (kind) => {
    setBusy(kind);
    try {
      const res = await api.post(`/assembly/${kind}`, { ...sel, ...opts, keys, glass_id: glass, design_id: designId || undefined }, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `assembly_details.${kind}`;
      a.click();
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setBusy(null);
    }
  };
  const set = (k) => (v) => setSel((s) => ({ ...s, [k]: v }));

  return (
    <div className="space-y-5" data-testid="assembly-details">
      <div className="grid gap-3 rounded-lg border border-zinc-800 bg-[#121215] p-4 md:grid-cols-3">
        <ProfileSelect label="Mullion (Bhoruka)" value={sel.mullion_id} onChange={(v) => { setSel((s) => ({ ...s, mullion_id: v, transom_id: null, sleeve_id: null })); setOpts((o) => ({ ...o, plate_id: null, cap_id: null, subframe_id: null })); }} options={mullions} testid="asm-mullion-select" />
        <ProfileSelect label="Transom" value={sel.transom_id} onChange={set("transom_id")} options={withCurrent(compat.transoms?.length ? compat.transoms : mullions, sel.transom_id, [...mullions, ...transomPool])} testid="asm-transom-select" allowNone />
        <ProfileSelect label="Splice sleeve" value={sel.sleeve_id} onChange={set("sleeve_id")} options={compat.sleeves || []} testid="asm-sleeve-select" allowNone />
        <ProfileSelect label="Vent sash" value={sel.sash_id} onChange={set("sash_id")} options={compat.sashes || []} testid="asm-sash-select" allowNone />
        <ProfileSelect label="Corner mullion" value={sel.corner_id} onChange={set("corner_id")} options={compat.corners || []} testid="asm-corner-select" allowNone />
        <label className="space-y-1">
          <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Glazing system</span>
          <Select value={opts.system} onValueChange={setO("system")}>
            <SelectTrigger data-testid="asm-system-select" className="h-9 border-zinc-800 bg-zinc-900 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="structural">4-way structural (semi-unitized SSG)</SelectItem>
              <SelectItem value="conventional">Conventional pressure plate + cap</SelectItem>
              <SelectItem value="two_way">2-way (SSG vertical, capped horizontal)</SelectItem>
            </SelectContent>
          </Select>
        </label>
        {opts.system !== "conventional" && <ProfileSelect label="SSG sub-frame" value={opts.subframe_id} onChange={setO("subframe_id")} options={compat.subframes || []} testid="asm-subframe-select" allowNone />}
        {opts.system !== "structural" && <ProfileSelect label="Pressure plate" value={opts.plate_id} onChange={setO("plate_id")} options={compat.plates || []} testid="asm-plate-select" allowNone />}
        {opts.system !== "structural" && <ProfileSelect label="Cover cap" value={opts.cap_id} onChange={setO("cap_id")} options={compat.caps || []} testid="asm-cap-select" allowNone />}
        <ProfileSelect label="Sash glass holder" value={opts.bead_id} onChange={setO("bead_id")} options={compat.beads || []} testid="asm-bead-select" allowNone />
        <label className="space-y-1">
          <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Sash type</span>
          <Select value={opts.sash_type} onValueChange={setO("sash_type")}>
            <SelectTrigger data-testid="asm-sash-type-select" className="h-9 border-zinc-800 bg-zinc-900 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{["top_hung", "side_hung", "bottom_hung"].map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
        </label>
        <label className="space-y-1">
          <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Corner angle (°)</span>
          <Select value={String(opts.corner_angle)} onValueChange={(v) => setO("corner_angle")(Number(v))}>
            <SelectTrigger data-testid="asm-corner-angle-select" className="h-9 border-zinc-800 bg-zinc-900 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{[90, 105, 120, 135, 150, 165].map((t) => <SelectItem key={t} value={String(t)}>{t}°</SelectItem>)}</SelectContent>
          </Select>
        </label>
        <div className="md:row-span-2"><span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Glass</span><GlassPicker value={glass} onChange={setGlass} testid="asm-glass" /></div>
        <div className="flex flex-wrap gap-x-4 gap-y-2 md:col-span-2">
          {KEYS.map(([k, l0]) => { const l = l0.replace("90°", `${opts.corner_angle}°`); return (
            <label key={k} className="flex items-center gap-2 text-xs text-zinc-300">
              <Checkbox data-testid={`asm-key-${k}`} checked={keys.includes(k)} onCheckedChange={(c) => setKeys((ks) => (c ? [...ks, k] : ks.filter((x) => x !== k)))} />{l}
            </label>
          ); })}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {data?.parts && <span className="font-mono text-xs text-sky-300" data-testid="asm-parts-info">Bhoruka parts: {partsText}</span>}
        {data && <span className="font-mono text-xs text-zinc-400" data-testid="asm-glass-info">{data.glass.total_mm} mm glass · gasket pocket {data.glass.gasket.pocket_mm} mm ({data.glass.gasket.inner_gasket_mm}+{data.glass.gasket.outer_gasket_mm} gaskets)</span>}
        <div className="ml-auto flex gap-2">
          <Button data-testid="asm-download-dxf" size="sm" variant="outline" disabled={!!busy || !data} onClick={() => download("dxf")} className="border-zinc-700 hover:text-white">{busy === "dxf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}Details DXF</Button>
          <Button data-testid="asm-download-pdf" size="sm" variant="outline" disabled={!!busy || !data} onClick={() => download("pdf")} className="border-zinc-700 hover:text-white">{busy === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}Details PDF</Button>
        </div>
      </div>
      <AssemblyNotes key={JSON.stringify([sel, opts, keys, glass])} payload={{ ...sel, ...opts, keys, glass_id: glass, design_id: designId || undefined }} />
      <div className="grid gap-4 2xl:grid-cols-2">
        {data?.details.map((d) => (
          <div key={d.key} className="rounded-lg border border-zinc-800 bg-[#0c0c0f] p-3">
            <div className="mb-2 font-mono text-[11px] uppercase tracking-wider text-zinc-400">{d.title}</div>
            {d.info && <InfoLine info={d.info} hide={HIDE} testid={`detail-info-${d.key}`} />}
            {d.info?.warning && <div className="mb-2 rounded border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-200" data-testid={`detail-warning-${d.key}`}>{d.info.warning}</div>}
            <DetailSVG detail={d} />
          </div>
        ))}
      </div>
    </div>
  );
}
